import type { Feed } from '~/services/layout/index.ts';
import type { ServerLoadedFeeds } from '~/services/layout/server-loaded-feeds.ts';
import type { FormDataModel } from './form-data-model.ts';
import type { FormUiComponent } from './types.ts';

/**
 * Adds feed-level role and extensions to each entry so preference editor cells
 * behave the same as server-loaded feeds passed through addWebLinks.
 *
 * @param feed - Synthetic form component feed.
 * @returns Feed with enriched entries.
 */
export function enrichFormScreenFeed(feed: Feed | null): Feed | null {
  if (!feed) return feed;

  const feedExtensions = feed.extensions ?? {};
  const role =
    typeof feedExtensions.role === 'string' ? feedExtensions.role : undefined;

  return {
    ...feed,
    entry:
      feed.entry?.map((entry) => ({
        ...entry,
        _role: role,
        _extensions: feedExtensions,
      })) ?? [],
  };
}

/**
 * Loads synthetic feeds for all dynamic form screen components.
 *
 * @param model - Active form data model.
 * @param components - UI components to resolve feeds for.
 * @returns Loaded feed entries keyed by component id.
 */
export async function loadFormScreenComponentFeeds(
  model: FormDataModel,
  components: FormUiComponent[]
): Promise<ServerLoadedFeeds> {
  return Promise.all(
    components.map(async (component) => {
      try {
        const feed = enrichFormScreenFeed(
          await model.getDataSource(component.id)()
        );
        return { componentId: component.id, feed };
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        return { componentId: component.id, feed: null, error: message };
      }
    })
  );
}
